/**
 * Zgłoszenia ubezpieczeniowe: ZUS ZUA, ZZA, ZWUA, ZIUA i ZCNA (Etap 16, 25, 30).
 *
 * Od Etapu 31 przekazany dokument da się poprawić: użytkownik wskazuje, CO było
 * błędne, a system wybiera drogę z poradników ZUS — jeden dokument w trybie
 * korekty albo parę „wyrejestrowanie i zgłoszenie od nowa" (ADR-0032).
 *
 * Sekcja modułu ZUS/PIT/PPK, ale rządzi się innym zegarem niż deklaracje:
 * zgłoszenie jest zdarzeniem jednej osoby z terminem 7 dni od powstania albo
 * ustania obowiązku ubezpieczeń. Dlatego lista pokazuje termin przy każdym
 * wierszu i mówi wprost, co jest po terminie.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  InfoBanner,
  Modal,
  ShieldPlus,
  StatusPill,
  Users,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { REGISTRATION_CORRECTION_SCOPES, correctionPath, warsawDay } from '@k1p4/shared'
import type { RegistrationCorrectionScope, ZusRegistrationKind } from '@k1p4/shared'
import { ApiError, api, apiToday, type ZusRegistrationItem } from '../../api/client'
import { useHasPermission } from '../../auth/AuthProvider'
import styles from './Declarations.module.css'

const STATUS_TONE: Record<string, 'neutral' | 'pink' | 'success' | 'danger'> = {
  todo: 'neutral',
  gen: 'pink',
  sent: 'success',
  err: 'danger',
}

export function ZusRegistrations() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canRun = useHasPermission('Automat : Deklaracje')

  const view = useQuery({
    queryKey: ['zus-registrations'],
    queryFn: () => api.zusRegistrations(),
  })

  const generate = useMutation({
    mutationFn: () => api.generateZusRegistrations(),
    onSuccess: (result) => {
      const removed = result.removed ?? 0
      toast.show(
        [
          t('hrRegistrations.generated', { count: result.generated, blocked: result.blocked }),
          /* Wiersze, które przestały wynikać z akt, kasujemy — i mówimy ile (#8). */
          removed > 0 ? t('hrRegistrations.generatedRemoved', { count: removed }) : null,
        ]
          .filter(Boolean)
          .join(' '),
        { tone: result.blocked > 0 ? 'info' : 'success' },
      )
      void queryClient.invalidateQueries({ queryKey: ['zus-registrations'] })
    },
  })

  const markSent = useMutation({
    mutationFn: (ids: string[]) => api.markZusRegistrationsSent(ids),
    onSuccess: (result) => {
      const skipped = result.skipped ?? 0
      toast.show(
        [
          t('hrRegistrations.markedSent', { count: result.marked }),
          /* Dokument, którego nie ma w pliku KEDU, nie poszedł do ZUS-u (#8). */
          skipped > 0 ? t('hrRegistrations.markedSkipped', { count: skipped }) : null,
        ]
          .filter(Boolean)
          .join(' '),
        { tone: skipped > 0 ? 'info' : 'success' },
      )
      void queryClient.invalidateQueries({ queryKey: ['zus-registrations'] })
    },
  })

  /* Plik KEDU przez fetch: odmowa ma zdanie, a nie surowy JSON w karcie (przegląd Fali 2). */
  const downloadKedu = useMutation({
    mutationFn: () => api.downloadRegistrationsKedu(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['zus-registrations'] }),
    onError: (cause) =>
      toast.show(
        t(
          cause instanceof ApiError && cause.code === 'NOTHING_TO_EXPORT'
            ? 'hrRegistrations.keduNothing'
            : cause instanceof ApiError && cause.code === 'KEDU_INCOMPLETE'
              ? 'hrRegistrations.keduIncomplete'
              : 'hrRegistrations.keduError',
        ),
        { tone: 'danger' },
      ),
  })

  /* Korekta przekazanego zgłoszenia (Etap 31): najpierw pytamy, co było błędne. */
  const [correcting, setCorrecting] = useState<{
    id: string
    kind: ZusRegistrationKind
    label: string
  } | null>(null)

  const correct = useMutation({
    mutationFn: (input: { id: string; scope: RegistrationCorrectionScope }) =>
      api.correctZusRegistration(input.id, input.scope),
    onSuccess: (result) => {
      toast.show(
        t(`hrRegistrations.corrected.${result.path}`, { count: result.created.length }),
        { tone: 'success' },
      )
      setCorrecting(null)
      void queryClient.invalidateQueries({ queryKey: ['zus-registrations'] })
    },
  })

  if (view.isPending || view.isError || !view.data) return null

  const data = view.data
  /* Dzień w Polsce — ten sam, którym API liczy termin i „po terminie". */
  const today = warsawDay(apiToday())
  const ready = data.items
    .filter((item) => item.status === 'gen' && item.id !== null)
    .map((item) => item.id!)
  /*
   * Do wystawienia: kandydaci z akt i wiersze z brakami — po uzupełnieniu akt
   * „Wystaw zgłoszenia" składa je od nowa, innej drogi nie ma (#8).
   */
  const toIssue = data.pendingCount + (data.recomputeCount ?? 0)

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>{t('hrRegistrations.title')}</h3>
        {data.overdueCount > 0 ? (
          <Badge tone="warning">
            {t('hrRegistrations.overdue', { count: data.overdueCount })}
          </Badge>
        ) : null}
      </div>
      <p className={styles.sectionNote}>{t('hrRegistrations.lead')}</p>

      {data.items.length === 0 ? (
        <EmptyState title={t('hrRegistrations.empty')} description={t('hrRegistrations.emptyBody')} />
      ) : (
        <Card pad="flush" className={styles.list}>
          {data.items.map((item) => (
            <RegistrationRow
              key={`${item.kind}:${item.employeeId}:${item.effectiveOn}:${item.mode}:${item.setNumber}`}
              item={item}
              today={today}
              {...(canRun
                ? {
                    onCorrect: (row: ZusRegistrationItem) =>
                      setCorrecting({
                        id: row.id!,
                        kind: row.kind,
                        label: `${t(`hrRegistrations.kinds.${row.kind}`)} · ${row.employeeName}`,
                      }),
                  }
                : {})}
            />
          ))}
        </Card>
      )}

      {/*
        Wybór zakresu korekty. Ekran mówi, jaka droga z niego wynika — bo to
        nie nasza decyzja, tylko treść poradników ZUS (ADR-0032).
      */}
      <Modal
        open={correcting !== null}
        onClose={() => setCorrecting(null)}
        title={t('hrRegistrations.correctTitle', { document: correcting?.label ?? '' })}
      >
        <p className={styles.sectionNote}>{t('hrRegistrations.correctLead')}</p>
        <div className={styles.sectionActions}>
          {REGISTRATION_CORRECTION_SCOPES.filter(
            (scope) => correcting !== null && correctionPath(correcting.kind, scope) !== null,
          ).map((scope) => (
            <Button
              key={scope}
              variant="secondary"
              disabled={correct.isPending}
              onClick={() => correct.mutate({ id: correcting!.id, scope })}
            >
              {t(`hrRegistrations.correctionScopes.${scope}`)}
            </Button>
          ))}
        </div>
        {correcting !== null &&
        REGISTRATION_CORRECTION_SCOPES.some(
          (scope) => correctionPath(correcting.kind, scope) === 'wyrejestrowanie_i_zgloszenie',
        ) ? (
          <InfoBanner tone="info">{t('hrRegistrations.correctPairNote')}</InfoBanner>
        ) : null}
      </Modal>

      {canRun ? (
        <div className={styles.sectionActions}>
          <Button
            variant="secondary"
            disabled={generate.isPending || toIssue === 0}
            onClick={() => generate.mutate()}
          >
            {/* Same wiersze do przeliczenia: przycisk mówi, że wystawia ponownie (grupa 8). */}
            {data.pendingCount === 0 && toIssue > 0
              ? t('hrRegistrations.regenerate', { count: toIssue })
              : t('hrRegistrations.generate', { count: toIssue })}
          </Button>
          <Button
            variant="secondary"
            disabled={ready.length === 0}
            onClick={() => downloadKedu.mutate()}
          >
            {t('hrRegistrations.downloadKedu')}
          </Button>
          <Button
            disabled={ready.length === 0 || markSent.isPending}
            onClick={() => markSent.mutate(ready)}
          >
            {t('hrRegistrations.markSent', { count: ready.length })}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function RegistrationRow({
  item,
  today,
  onCorrect,
}: {
  item: ZusRegistrationItem
  today: string
  onCorrect?: (item: ZusRegistrationItem) => void
}) {
  const { t } = useTranslation()
  const overdue = item.sentAt === null && item.deadline < today
  const blocking = item.issues.filter((issue) => issue.blocking)
  const notes = item.issues.filter((issue) => !issue.blocking)

  return (
    /*
     * Własna siatka z ikoną (#22): bez kolumny ikony treść lądowała w 38 px
     * pierwszej kolumny i łamała się słowo po słowie.
     */
    <div className={cx(styles.row, styles.rowCompact)}>
      <span className={styles.rowIcon} aria-hidden="true">
        {item.kind === 'zcna' ? <Users size={18} /> : <ShieldPlus size={18} />}
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>
          {t(`hrRegistrations.kinds.${item.kind}`)} · {item.employeeName}
          {/* ZCNA dotyczy konkretnego członka rodziny — bez tego wiersz milczy. */}
          {item.memberLabel ? ` · ${item.memberLabel}` : ''}
          {/* Tryb mówi, czym ten dokument jest: zgłoszeniem, zmianą czy korektą. */}
          {item.mode !== 'zgloszenie' ? ` · ${t(`hrRegistrations.modes.${item.mode}`)}` : ''}
        </span>
        <span className={styles.rowMeta}>
          {t('hrRegistrations.effectiveOn', { date: formatDate(item.effectiveOn, 'dot') })}
          {/* ZIUA nie ma kodu tytułu — dotyczy danych osoby, nie ubezpieczenia. */}
          {item.insuranceCode !== null
            ? ` · ${t('hrRegistrations.insuranceCode', { code: item.insuranceCode })}`
            : ''}
          {item.reasonCode !== null ? ` · ${t('hrRegistrations.reason', { code: item.reasonCode })}` : ''}
        </span>
        <span className={overdue ? styles.rowIssue : styles.rowMeta}>
          {t('hrRegistrations.deadline', { date: formatDate(item.deadline, 'dot') })}
        </span>
        {/*
          ZIUA: mówimy CO się zmieniło i z czego na co — bez tego zgłoszenie
          zmiany danych jest wierszem bez treści (Etap 25).
        */}
        {item.identityChange !== undefined && item.identityChange !== null ? (
          <span className={styles.rowMeta}>
            {t('hrRegistrations.identityChange', {
              fields: item.identityChange.fields
                .map((field) => t(`hrRegistrations.identityFields.${field}`))
                .join(', '),
              previous: `${item.identityChange.previous.lastName} ${item.identityChange.previous.firstName}`,
              next: `${item.identityChange.next.lastName} ${item.identityChange.next.firstName}`,
            })}
          </span>
        ) : null}
        {/*
          Wypisujemy WSZYSTKIE braki, nie pierwszy z listy: użytkownik ma je
          uzupełnić w jednym przejściu, a nie odkrywać po jednym na raz.
        */}
        {blocking.map((issue) => (
          <span key={issue.kind} className={styles.rowIssue}>
            {t(`hrRegistrations.issues.${issue.kind}`)}
          </span>
        ))}
        {/* Zastrzeżenia nieblokujące: dokument powstaje, ale trzeba coś sprawdzić. */}
        {notes.map((issue) => (
          <span key={issue.kind} className={styles.rowNote}>
            {t(`hrRegistrations.issues.${issue.kind}`)}
          </span>
        ))}
      </span>
      <span className={styles.rowActions}>
        <StatusPill tone={STATUS_TONE[item.status] ?? 'neutral'}>
          {t(`hrRegistrations.status.${item.status}`)}
        </StatusPill>
        {/*
          Poprawić da się tylko dokument PRZEKAZANY — niewysłany wystarczy
          wystawić ponownie, więc przycisku wtedy nie ma [D28].
        */}
        {onCorrect && item.id !== null && item.sentAt !== null ? (
          <Button size="sm" variant="secondary" onClick={() => onCorrect(item)}>
            {t('hrRegistrations.correct')}
          </Button>
        ) : null}
      </span>
    </div>
  )
}
